import { useEffect, useRef, useState } from 'react';

import { getBulkShippingLabel, getTakeBackBagDetails } from '../../utils/service';
import {
  GOLOCKER_STATUS_POLL,
  dropOffStatusKey,
  isDropOffStatusFinal,
  toBagDropOffStatus,
  toBatchDropOffStatus,
} from '../../utils/goLocker';

const LOG_PREFIX = '[useGoLockerDropOffStatus]';

const isPageHidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';

/**
 * useGoLockerDropOffStatus — the G5/G6 screen's live status for one bag (`code`) or a
 * batch (`batchId`), polled so it walks G6 → Dropped off → Picked up → Delivered! on
 * its own (addendum 4):
 *
 *   - GET /redemptions/:code, or /redemptions/bulk/:batchId for a batch;
 *   - every 15s while no drop-off is recorded, every 60s once dropped off, and not at
 *     all once Delivered!;
 *   - gives up after 30 minutes of G6 with no change (the screen stays as it is; the
 *     next open starts over);
 *   - paused while the tab is hidden, refetched the moment it is visible again;
 *   - one request at a time; a failed poll changes nothing and the next tick retries.
 *
 * Returns { status, loading, failed, retry }. `status` is null until the first answer
 * lands. `failed` turns true after `failuresBeforeError` consecutive failures with no
 * status yet (polling carries on, and a later answer clears it); `retry()` resets it and
 * fetches now, also restarting polling that gave up.
 */
const useGoLockerDropOffStatus = ({ code, batchId, enabled = true } = {}) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(Boolean(enabled && (code || batchId)));
  const [failed, setFailed] = useState(false);
  const retryRef = useRef(() => {});

  const timerRef = useRef(null);
  const stoppedRef = useRef(false);
  const lastKeyRef = useRef(null);
  const lastChangeAtRef = useRef(0);
  const statusRef = useRef(null);

  useEffect(() => {
    if (!enabled || (!code && !batchId)) return undefined;

    let active = true;
    // Per run, not a ref: a request still open for the PREVIOUS identifier must not
    // block this run's first tick — that run's finally skips schedule() once it is
    // inactive, so nothing would ever arm a timer for the new identifier.
    let inFlight = false;
    let failures = 0;
    stoppedRef.current = false;
    lastKeyRef.current = null;
    lastChangeAtRef.current = Date.now();
    statusRef.current = null;
    setStatus(null);
    setLoading(true);
    setFailed(false);

    const clear = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
    };

    // Called after every answer (or failure): decides the next tick, or stops.
    // eslint-disable-next-line no-use-before-define
    const schedule = () => {
      clear();
      if (!active || stoppedRef.current) return;
      const { current } = statusRef;
      if (isDropOffStatusFinal(current)) {
        stoppedRef.current = true;
        return;
      }
      const waitingOnLocker = !current?.dropOff;
      if (waitingOnLocker
        && Date.now() - lastChangeAtRef.current >= GOLOCKER_STATUS_POLL.pendingGiveUpMs) {
        stoppedRef.current = true;
        return;
      }
      if (isPageHidden()) return; // resumed by the visibility listener
      // eslint-disable-next-line no-use-before-define
      timerRef.current = setTimeout(tick, waitingOnLocker
        ? GOLOCKER_STATUS_POLL.pendingMs
        : GOLOCKER_STATUS_POLL.droppedOffMs);
    };

    const tick = async () => {
      if (!active || inFlight) return;
      inFlight = true;
      try {
        const { data, error } = batchId
          ? await getBulkShippingLabel(batchId)
          : await getTakeBackBagDetails(code);
        if (!active) return;
        // A failed poll keeps whatever is on screen; the next tick retries.
        if (error || !data) {
          console.warn(`${LOG_PREFIX} status fetch failed for ${batchId ?? code}:`, error);
          failures += 1;
        } else {
          failures = 0;
          const next = batchId ? toBatchDropOffStatus(data) : toBagDropOffStatus(data, code);
          const key = dropOffStatusKey(next);
          if (key !== lastKeyRef.current) {
            lastKeyRef.current = key;
            lastChangeAtRef.current = Date.now();
            statusRef.current = next;
            setStatus(next);
          }
        }
      } catch (e) {
        // The service helpers resolve { data, error }; this only guards a surprise.
        console.warn(`${LOG_PREFIX} status fetch threw for ${batchId ?? code}:`, e);
        failures += 1;
      } finally {
        inFlight = false;
        if (active) {
          setLoading(false);
          // Only with nothing on screen: once a status is shown, a failed poll keeps it.
          setFailed(!statusRef.current && failures >= GOLOCKER_STATUS_POLL.failuresBeforeError);
          schedule();
        }
      }
    };

    const onVisibility = () => {
      if (isPageHidden()) {
        clear();
      } else if (!stoppedRef.current) {
        clear();
        tick();
      }
    };

    retryRef.current = () => {
      if (!active) return;
      // One short of the threshold: after an explicit Retry, a single failure brings the
      // error back at once instead of ~30s more of spinner.
      failures = GOLOCKER_STATUS_POLL.failuresBeforeError - 1;
      setFailed(false);
      stoppedRef.current = false;
      lastChangeAtRef.current = Date.now();
      clear();
      tick();
    };

    document.addEventListener('visibilitychange', onVisibility);
    if (isPageHidden()) {
      setLoading(false);
    } else {
      tick();
    }

    return () => {
      active = false;
      clear();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [code, batchId, enabled]);

  return { status, loading, failed, retry: () => retryRef.current() };
};

export default useGoLockerDropOffStatus;
